import React from 'react';
import { Box, Typography } from '@mui/material';
import CapabilityTrend from './CapabilityTrend';
import { hasTrend, tierHistoryPoints, trendWindow } from './capabilityTrendData';
import { MEDALS, type MedalKey, type TierHistoryPoint } from '../../../../../../components/gamification/scorecardData';
import { ChartSection, ScoreCard, ScoreLine, SectionDivide, SectionTitle } from './panelSections';
import { MathRow, Op, TIP_HEADING, TIP_TEXT } from './TeamCapabilityScore';
import { RECENCY_MEASURES } from './activityMeasures';
import {
  activityTiers,
  tierForPercent,
  type ActivityAudience,
  type ActivityMeasure,
  type TeamActivity
} from './teamActivity';

const tierText = (key: MedalKey) => ({ text: MEDALS[key].label, color: MEDALS[key].color });

const tierName = (key: MedalKey, current: MedalKey | null) => (
  <Box component="span" sx={{ color: MEDALS[key].color, fontWeight: key === current ? 700 : 600 }}>
    {MEDALS[key].label}
    {key === current ? ' (now)' : ''}
  </Box>
);

/** "Low 0–24%" and so on: the bracket each tier covers, the current one picked out. */
const TierBrackets: React.FC<{ current: MedalKey | null; audience: ActivityAudience }> = ({ current, audience }) => (
  <Box>
    <Typography sx={TIP_HEADING}>Tiers</Typography>
    {activityTiers(audience).map((t) => (
      <MathRow key={t.key} label={tierName(t.key, current)} value={`${t.from}–${t.to}%`} />
    ))}
  </Box>
);

// How a learner's recency measures are tiered: by when they last did it, not by a %.
const RECENCY_UNIT: Record<string, { last: string; many: string }> = {
  'Proactive Learning': { last: 'Last message to Elliott', many: '3+ messages in the last 7 days' },
  Roleplays: { last: 'Last Roleplay completed', many: '3+ Roleplays in the last 7 days' },
  'Practice Rounds': { last: 'Last practice round', many: '3+ practice rounds in the last 7 days' }
};

/** A learner's recency measure: when they last did it, the tier that gives, and the day bands. */
const recencyTooltip = (measure: ActivityMeasure) => {
  const unit = RECENCY_UNIT[measure.name];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 250 }}>
      <Box>
        <Typography sx={TIP_HEADING}>Breakdown</Typography>
        <MathRow label={unit.last} value={measure.value || '–'} />
        <MathRow
          total
          label="Tier"
          value={
            <Box component="span" sx={{ color: MEDALS[measure.tier].color }}>
              {MEDALS[measure.tier].label}
            </Box>
          }
        />
      </Box>
      <Box>
        <Typography sx={TIP_HEADING}>Tiers</Typography>
        <MathRow label={tierName('wood', measure.tier)} value="None in 30 days" />
        <MathRow label={tierName('bronze', measure.tier)} value="Within 30 days" />
        <MathRow label={tierName('silver', measure.tier)} value="Within 14 days" />
        <MathRow label={tierName('gold', measure.tier)} value="Within 7 days" />
        <MathRow label={tierName('platinum', measure.tier)} value={unit.many} />
      </Box>
    </Box>
  );
};

/** One measure's own tooltip: its %, the tier that puts it in, and the brackets. */
const percentTooltip = (measure: ActivityMeasure, audience: ActivityAudience) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 220 }}>
    <Box>
      <Typography sx={TIP_HEADING}>Breakdown</Typography>
      <MathRow label={measure.label} value={`${measure.percent}%`} />
      <MathRow
        total
        label="Tier"
        value={
          <Box component="span" sx={{ color: MEDALS[measure.tier].color }}>
            {MEDALS[measure.tier].label}
          </Box>
        }
      />
    </Box>
    <TierBrackets current={measure.tier} audience={audience} />
  </Box>
);

const measureTooltip = (measure: ActivityMeasure, audience: ActivityAudience) =>
  audience === 'learner' && RECENCY_MEASURES.has(measure.name)
    ? recencyTooltip(measure)
    : percentTooltip(measure, audience);

/**
 * The Activity tab's lead-in, in the Capability tab's design: the Activity
 * tier card (the % and how it is worked out behind the underline), the
 * Last 6 Months chart with the tiers on its y axis, then the title for the
 * measures list that follows. Shared by the admin and the learner dashboards.
 */
const TeamActivitySections: React.FC<{
  activity: TeamActivity;
  history?: TierHistoryPoint[] | null;
  audience?: ActivityAudience;
  /** An admin looking at one member's Activity: their name, so the copy reads in the third person. */
  memberName?: string;
}> = ({ activity, history, audience = 'admin', memberName }) => {
  const window = trendWindow();
  const tiers = activityTiers(audience);
  // The chart's y axis for tiers: a line at each bracket edge, each tier named mid-band.
  const tierAxis = {
    lines: [0, ...tiers.map((t) => Math.min(100, t.to + 1))],
    labels: tiers.map((t) => ({ at: (t.from + Math.min(100, t.to + 1)) / 2, text: MEDALS[t.key].label }))
  };
  const learner = audience === 'learner';
  const points = tierHistoryPoints(window, history ?? [], activity.percent);

  const scoreTooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 300 }}>
      <Box>
        <Typography sx={TIP_HEADING}>Breakdown</Typography>
        <Typography sx={{ ...TIP_TEXT, fontSize: 10.5, color: '#8A8A8A', mt: '4px', mb: '2px' }}>
          {learner && !memberName
            ? 'Each measure counts equally toward your total.'
            : 'Each measure counts equally toward the total.'}
        </Typography>
        {activity.measures.map((m) => (
          <MathRow
            key={m.name}
            label={m.label}
            value={
              <>
                {m.percent}%<Op>×</Op>
                {m.weight}%<Op>=</Op>
                {m.contribution}
              </>
            }
          />
        ))}
        {activity.percent !== null && (
          <MathRow
            total
            label={learner ? 'My Activity' : 'Team Activity'}
            value={
              <>
                {activity.measures.map((m, i) => (
                  <React.Fragment key={m.name}>
                    {i > 0 && <Op>+</Op>}
                    {m.contribution}
                  </React.Fragment>
                ))}
                <Op>=</Op>
                {activity.percent}%
              </>
            }
          />
        )}
        {learner && (
          <Typography sx={{ ...TIP_TEXT, fontSize: 10.5, color: '#8A8A8A', mt: '6px' }}>
            Questions to Elliott, Roleplays and Practice Rounds are scored by how recently {memberName ? 'they' : 'you'}{' '}
            last did each one. Hover a measure for its bands.
          </Typography>
        )}
      </Box>
      <TierBrackets current={activity.tier} audience={audience} />
    </Box>
  );

  return (
    <>
      <ScoreCard
        percent={activity.percent}
        tier={activity.tier ? tierText(activity.tier) : undefined}
        tooltip={scoreTooltip}
      >
        {activity.strongest && (
          <ScoreLine
            label="Strongest Activity"
            name={activity.strongest.label}
            percent={activity.strongest.percent}
            tier={tierText(activity.strongest.tier)}
            tooltip={measureTooltip(activity.strongest, audience)}
          />
        )}
        {activity.weakest && (
          <ScoreLine
            label="Weakest Activity"
            name={activity.weakest.label}
            percent={activity.weakest.percent}
            tier={tierText(activity.weakest.tier)}
            tooltip={measureTooltip(activity.weakest, audience)}
          />
        )}
      </ScoreCard>
      <SectionDivide />

      {/* The same chart as Capability; only the y axis differs, naming the tiers. */}
      <ChartSection>
        <CapabilityTrend
          compact
          title="Last 6 Months"
          // Until there are two weeks to compare, the same chart stands empty and says when it will fill.
          note={hasTrend(points) ? undefined : 'The trend appears here once a second week of activity is recorded.'}
          window={window}
          series={[{ key: 'capability', label: 'Activity', points }]}
          yAxis={tierAxis}
          formatValue={(v) => MEDALS[tierForPercent(v, audience)].label}
          // On hover, the tier with the % behind it.
          formatHover={(v) => `${MEDALS[tierForPercent(v, audience)].label} ${Math.round(v)}%`}
        />
      </ChartSection>
      <SectionDivide />

      <SectionTitle>Activity</SectionTitle>
    </>
  );
};

export default TeamActivitySections;
